import React, { useState } from 'react';
import { WEBHOOKS } from '../config/ambiente';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Matriz GUT — Gravidade × Urgência × Tendência.
//
// Exigida no envio quando o tipo é Suporte avançado COM "possível falha de
// sistema": é o número que ordena a fila do Setor de Sistemas. Sem ele, tudo
// que chega lá tem a mesma prioridade aparente.
//
// A IA calcula sob demanda (botão), não sozinha: o operador vê a sugestão e
// decide se aplica ou preenche à mão. As escalas são as mesmas do 1.4.1 —
// mudar um rótulo aqui muda o significado do número no relatório.
// ─────────────────────────────────────────────────────────────────────────────

export const ESCALA_G = [
  '1 - Sem gravidade', '2 - Pouco grave', '3 - Grave',
  '4 - Muito grave', '5 - Extremamente grave',
];
export const ESCALA_U = [
  '1 - Pode esperar', '2 - Pouco urgente', '3 - O mais rápido possível',
  '4 - Muito urgente', '5 - Precisa de ação imediata',
];
export const ESCALA_T = [
  '1 - Não irá mudar', '2 - Irá piorar a longo prazo', '3 - Irá piorar',
  '4 - Irá piorar em pouco tempo', '5 - Irá piorar rapidamente',
];

function Seletor({ label, valor, aoMudar, escala }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <select value={valor} onChange={e => aoMudar(Number(e.target.value))}>
        {escala.map((texto, i) => (
          <option key={i + 1} value={i + 1}>{texto}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * O texto que vai para o fluxo do GUT.
 *
 * No 1.4.1 é `replyResult.value || userInput.value || 'Interação vazia.'`: o
 * ticket formalizado quando existe, senão as anotações cruas.
 */
function textoDoAtendimento(contexto) {
  return contexto?.texto_final || contexto?.anotacoes || 'Interação vazia.';
}

export default function MatrizGut({ gut, aoMudar, contexto }) {
  const [calculando, setCalculando] = useState(false);
  const [sugestao, setSugestao] = useState(null);
  const [erro, setErro] = useState('');

  const total = gut.g * gut.u * gut.t;

  /** Pede a recomendação à IA. Ela sugere; quem aplica é o operador. */
  const calcular = async () => {
    if (calculando) return;
    setCalculando(true);
    setErro('');
    setSugestao(null);
    try {
      // O fluxo do GUT é o mesmo do 1.4.1: recebe APENAS `anotacoes` (o texto
      // do atendimento) e responde em TEXTO, não em JSON —
      // "GUT: G(4) * U(3) * T(2) Justificativa: ...". Ler com r.json() ou
      // mandar campos a mais faz o fluxo cair fora do caminho esperado.
      const res = await fetch(WEBHOOKS.gut, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anotacoes: textoDoAtendimento(contexto) }),
      });
      if (!res.ok) throw new Error(`o fluxo respondeu ${res.status}`);

      const txt = await res.text();
      const achado = txt.match(/GUT:\s*G\((\d+)\)\s*\*\s*U\((\d+)\)\s*\*\s*T\((\d+)\)/i);
      const just = txt.match(/Justificativa:\s*([^"}]*)/i);

      if (!achado) {
        setErro('Não foi possível extrair a recomendação. Preencha à mão.');
        return;
      }

      const g = parseInt(achado[1], 10);
      const u = parseInt(achado[2], 10);
      const t = parseInt(achado[3], 10);
      const dentro = n => Number.isFinite(n) && n >= 1 && n <= 5;

      if (!dentro(g) || !dentro(u) || !dentro(t)) {
        setErro('A IA não devolveu uma classificação válida. Preencha à mão.');
        return;
      }
      setSugestao({ g, u, t, justificativa: just ? just[1].trim() : '' });
    } catch (e) {
      setErro(`Não foi possível calcular: ${e.message}`);
    } finally {
      setCalculando(false);
    }
  };

  return (
    <div className="panel gut-painel">
      <PanelHeadGut total={total} />

      <div className="acoes-fim" style={{ margin: '0 0 14px', justifyContent: 'flex-start' }}>
        <button className="btn btn-ghost btn-sm" onClick={calcular} disabled={calculando}>
          {calculando
            ? <><Icon name="rotate-cw" size={13} className="orion-spin" /> Consultando…</>
            : <><Icon name="zap" size={13} /> Calcular com IA</>}
        </button>
      </div>

      {erro && <div className="campo-dica" style={{ color: 'var(--warn)', marginBottom: 12 }}>{erro}</div>}

      {/* Verde, como toda sugestão da IA no app: é proposta, e só entra no
          cálculo se o operador clicar em aplicar. */}
      {sugestao && (
        <div className="panel panel-dest d-ia" style={{ marginBottom: 16 }}>
          <b>Sugestão da IA:</b> G{sugestao.g} × U{sugestao.u} × T{sugestao.t} = {sugestao.g * sugestao.u * sugestao.t}
          {sugestao.justificativa ? ` — ${sugestao.justificativa}` : ''}
          <div style={{ marginTop: 10 }}>
            <button
              className="btn btn-primary btn-sm"
              // A justificativa da IA vai junto: ela acompanha o GUT até o
              // incidente (campo `gut.justificativa` na webhook salvar-erro),
              // como no 1.4.1.
              onClick={() => {
                aoMudar({
                  g: sugestao.g, u: sugestao.u, t: sugestao.t,
                  justificativa: sugestao.justificativa || '',
                });
                setSugestao(null);
              }}
            >
              <Icon name="check" size={12} /> Aplicar sugestão
            </button>
          </div>
        </div>
      )}

      <div className="gut-editor">
        <Seletor label="Gravidade (G)" valor={gut.g} escala={ESCALA_G} aoMudar={g => aoMudar({ ...gut, g })} />
        <Seletor label="Urgência (U)" valor={gut.u} escala={ESCALA_U} aoMudar={u => aoMudar({ ...gut, u })} />
        <Seletor label="Tendência (T)" valor={gut.t} escala={ESCALA_T} aoMudar={t => aoMudar({ ...gut, t })} />
      </div>

      <div className="gut-result">
        G × U × T = <b>{total}</b> — é este número que ordena a fila do Setor de Sistemas.
      </div>
    </div>
  );
}

/** Cabeçalho com o total em destaque, no formato das pills do Nexus. */
function PanelHeadGut({ total }) {
  return (
    <div className="panel-head">
      <div className="panel-title">
        Classificação GUT <span className="sub">obrigatória para o Setor de Sistemas</span>
      </div>
      <span className={`gut ${total >= 100 ? 'gut-alto' : total >= 40 ? 'gut-medio' : 'gut-baixo'}`}>
        <span className="gut-k">GUT</span>
        <span className="gut-n">{total}</span>
      </span>
    </div>
  );
}
